
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { useFirebaseAuth, login as authLogin, logout as authLogout } from '../services/auth';

interface UserData {
    role?: string;
    schoolName?: string;
    [key: string]: any;
}

interface AuthContextType {
    currentUser: User | null;
    userData: UserData | null;
    loading: boolean;
    error: string | null;
    login: (email: string, password: string) => Promise<any>;
    logout: () => Promise<void>;
    clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children?: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const { currentUser, userData, loading: authLoading, error: authError } = useFirebaseAuth();
    const [error, setError] = useState<string | null>(null);
    const [isTimedOut, setIsTimedOut] = useState(false);

    // Timeout for loading
    useEffect(() => {
        const timer = setTimeout(() => {
            if (authLoading) {
                console.warn("Auth loading timed out.");
                setIsTimedOut(true);
            }
        }, 15000); // 15-second timeout

        return () => clearTimeout(timer);
    }, [authLoading]);

    const clearError = () => {
        setError(null);
        setIsTimedOut(false);
    };
    
    // Consolidate loading and error states
    let combinedError = authError || error;
    if (isTimedOut && authLoading) {
        combinedError = "Login process is taking too long. Please check your internet connection and try again.";
    }


    const value = {
        currentUser,
        userData,
        loading: authLoading && !isTimedOut,
        error: combinedError,
        login: authLogin,
        logout: authLogout,
        clearError,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
